/**
 * userTypes.js — the registry of BisaWofa user types and the extra
 * Add-Location fields each one contributes.
 *
 * A "user type" describes who the person adding content is (a general
 * visitor, a restaurant owner, a shopkeeper, …). Each type can declare
 * additional form fields that appear in the Add-Location modal on top of the
 * base fields, and the values are stored as key/value location_attributes so
 * new business types can be added later with zero schema changes.
 *
 * ── Identity & login (important) ─────────────────────────────────────────
 * The *current* user type comes from a single source: the local profile
 * setting `user_type` (see getUserType / setUserType in database.js). Until a
 * login exists, the user self-identifies via a Settings selector. When login
 * is implemented it simply writes the same `user_type` (and later a
 * server-verified role) — so nothing in the form logic below has to change.
 * This file is the one place to extend as new business types are onboarded.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * A type's `addLocationFields` are glassForm field descriptors (see
 * components/glassForm.js). Their `name` becomes the attribute key stored
 * against the location.
 *
 * @module userTypes
 */

export const DEFAULT_USER_TYPE = 'visitor';

/**
 * The registry. Add new business types here.
 * @type {Record<string, {
 *   id: string,
 *   label: string,
 *   icon: string,              // Bootstrap-icon class
 *   defaultCategory?: string,  // pre-selects the base "category" field
 *   addLocationFields: Array<object>  // extra glassForm field descriptors
 * }>}
 */
export const USER_TYPES = {
  visitor: {
    id: 'visitor',
    label: 'Visitor',
    icon: 'bi-person',
    addLocationFields: [],   // just the base fields
  },

  restaurant: {
    id: 'restaurant',
    label: 'Restaurant owner',
    icon: 'bi-shop',
    defaultCategory: 'restaurant',
    addLocationFields: [
      {
        name: 'menu_specials', id: 'al-menu-specials', label: 'Menu specials',
        type: 'textarea', voice: true,
        placeholder: "Today's specials, signature dishes…", maxlength: 500,
      },
      {
        name: 'cuisine', id: 'al-cuisine', label: 'Cuisine',
        type: 'text', voice: true,
        placeholder: 'e.g. Ghanaian, Continental, Chinese', maxlength: 120,
      },
      {
        name: 'price_range', label: 'Price range',
        type: 'select',
        options: ['', 'Budget (₵)', 'Mid-range (₵₵)', 'Premium (₵₵₵)'],
        placeholder: 'Select price range…',
      },
      {
        name: 'opening_hours', id: 'al-opening-hours', label: 'Opening hours',
        type: 'text',
        placeholder: 'e.g. Mon–Sat 08:00–22:00', maxlength: 160,
      },
    ],
  },

  // Future business types (shop, transport, pharmacy, …) slot in here with
  // their own addLocationFields. No other code needs to change.
};

/**
 * Resolve a user type id to its registry entry, falling back to the default.
 * @param {string} typeId
 * @returns {object}
 */
export function getUserTypeDef(typeId) {
  return USER_TYPES[typeId] || USER_TYPES[DEFAULT_USER_TYPE];
}

/**
 * The extra Add-Location field descriptors for a user type (empty for
 * visitors).
 * @param {string} typeId
 * @returns {Array<object>}
 */
export function getExtraLocationFields(typeId) {
  return getUserTypeDef(typeId).addLocationFields || [];
}

/**
 * Options for a user-type <select> (id/label pairs) for the Settings picker.
 * @returns {Array<{value:string, label:string}>}
 */
export function getUserTypeOptions() {
  return Object.values(USER_TYPES).map((t) => ({ value: t.id, label: t.label }));
}

export default { USER_TYPES, DEFAULT_USER_TYPE, getUserTypeDef, getExtraLocationFields, getUserTypeOptions };
